import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {LgPocWizardStepStatusEnum} from '../../../enum/lg-poc-wizard-step-status.enum';
import {
    LgPocSelectedLogic,
    LgPocWizardStateInterface
} from '../../../interface/lg-poc-wizard-state.interface';

@Injectable()
export class LgPocWizardStateService {

    private readonly initialState: LgPocWizardStateInterface = {
        step1: {status: LgPocWizardStepStatusEnum.PENDING},
        step2: {status: LgPocWizardStepStatusEnum.PENDING},
        step3: {status: LgPocWizardStepStatusEnum.PENDING},
        selectedLogics: []
    };

    private stateSubject = new BehaviorSubject<LgPocWizardStateInterface>({...this.initialState});

    state$: Observable<LgPocWizardStateInterface> = this.stateSubject.asObservable();

    get state(): LgPocWizardStateInterface {
        return this.stateSubject.getValue();
    }

    get currentStep(): number {
        const state = this.state;
        if (state.step3.status === LgPocWizardStepStatusEnum.IN_PROGRESS ||
            state.step3.status === LgPocWizardStepStatusEnum.COMPLETED ||
            state.step3.status === LgPocWizardStepStatusEnum.FAILED) {
            return 2;
        }
        if (state.step2.status === LgPocWizardStepStatusEnum.IN_PROGRESS ||
            state.step2.status === LgPocWizardStepStatusEnum.COMPLETED ||
            state.step2.status === LgPocWizardStepStatusEnum.FAILED) {
            return 1;
        }
        return 0;
    }

    initializeWizard(): void {
        this.stateSubject.next({
            ...this.initialState,
            step1: {status: LgPocWizardStepStatusEnum.IN_PROGRESS},
            selectedLogics: []
        });
    }

    // Step 1: Basics (name, alias, group, description)
    completeStep1(data: {
        name: string;
        alias: string;
        logicGroupId: number;
        description: string;
    }): void {
        this.stateSubject.next({
            ...this.state,
            name: data.name,
            alias: data.alias,
            logicGroupId: data.logicGroupId,
            description: data.description,
            step1: {status: LgPocWizardStepStatusEnum.COMPLETED},
            step2: {status: LgPocWizardStepStatusEnum.IN_PROGRESS}
        });
    }

    failStep1(errorMessage: string): void {
        this.stateSubject.next({
            ...this.state,
            step1: {status: LgPocWizardStepStatusEnum.FAILED, errorMessage}
        });
    }

    retryStep1(): void {
        this.stateSubject.next({
            ...this.state,
            step1: {status: LgPocWizardStepStatusEnum.IN_PROGRESS, errorMessage: undefined}
        });
    }

    // Step 2: Logic block selection + ordering → then create
    completeStep2(data: {
        selectedLogics: LgPocSelectedLogic[];
        typeId: number;
        typeName: string;
    }): void {
        this.stateSubject.next({
            ...this.state,
            selectedLogics: data.selectedLogics,
            createdTypeId: data.typeId,
            createdTypeName: data.typeName,
            step2: {status: LgPocWizardStepStatusEnum.COMPLETED},
            step3: {status: LgPocWizardStepStatusEnum.IN_PROGRESS}
        });
    }

    failStep2(errorMessage: string): void {
        this.stateSubject.next({
            ...this.state,
            step2: {status: LgPocWizardStepStatusEnum.FAILED, errorMessage}
        });
    }

    retryStep2(): void {
        this.stateSubject.next({
            ...this.state,
            step2: {status: LgPocWizardStepStatusEnum.IN_PROGRESS, errorMessage: undefined}
        });
    }

    // Step 3: Done screen (no action needed)
    completeStep3(): void {
        this.stateSubject.next({
            ...this.state,
            step3: {status: LgPocWizardStepStatusEnum.COMPLETED}
        });
    }

    goBackToStep1(): void {
        this.stateSubject.next({
            ...this.state,
            step2: {status: LgPocWizardStepStatusEnum.PENDING},
            step3: {status: LgPocWizardStepStatusEnum.PENDING},
            step1: {status: LgPocWizardStepStatusEnum.IN_PROGRESS}
        });
    }

    goBackToStep2(): void {
        this.stateSubject.next({
            ...this.state,
            step3: {status: LgPocWizardStepStatusEnum.PENDING},
            step2: {status: LgPocWizardStepStatusEnum.IN_PROGRESS}
        });
    }
}
